import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import Button from '@components/forms/Button';
import { ReactComponent as CheckIcon } from '@assets/icons/check_icon.svg';
import { ReactComponent as PlusIcon } from '@assets/icons/plus_icon.svg';
import { brokerGiveaway } from '@constants';
import { useAuthStore } from '@stores';
import { useBrokerPromo } from '../../hooks/useBrokerPromo';

interface BrokerPromoPanelProps {
  onAddLenders: () => void;
}

const BrokerPromoPanel = ({ onAddLenders }: BrokerPromoPanelProps) => {
  const navigate = useNavigate();
  const { isInspectionEnabled } = useAuthStore();
  const { eligible, days, hours, minutes } = useBrokerPromo();
  const canCreateInspection = isInspectionEnabled();

  if (!eligible) {
    return null;
  }

  const timeParts = [
    { value: days, label: 'Days' },
    { value: hours, label: 'Hrs' },
    { value: minutes, label: 'Min' }
  ];

  return (
    <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-campaign-from via-campaign-via to-campaign-to text-white mb-8">
      {/* Decorative concentric rings */}
      <div className="pointer-events-none absolute -top-40 -right-16 w-[520px] h-[520px] rounded-full border border-white/10" />
      <div className="pointer-events-none absolute -top-24 right-4 w-[360px] h-[360px] rounded-full border border-white/10" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-8 py-6">
        {/* Header band */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em]">
            <span className="w-1.5 h-1.5 rounded-full bg-campaign-gold" />
            {brokerGiveaway.eyebrow}
          </div>
          <div className="flex items-center gap-2.5">
            <span className="text-xs text-white/70">Draw closes in</span>
            <div className="flex gap-2">
              {timeParts.map((part) => (
                <div
                  key={part.label}
                  className="flex flex-col items-center rounded-md bg-white/10 border border-white/15 px-2.5 py-1 min-w-[44px]">
                  <span className="text-base font-bold leading-none">
                    {String(part.value).padStart(2, '0')}
                  </span>
                  <span className="text-[9px] uppercase tracking-wide text-white/60">{part.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-white/15 -mx-6 md:-mx-8" />

        {/* Main */}
        <div className="flex flex-col gap-8 pt-6 lg:flex-row lg:items-center lg:gap-10">
          {/* Left — offer + entries + actions */}
          <div className="lg:flex-1 lg:max-w-xl">
            <span className="inline-block bg-campaign-gold text-campaign-from text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-md">
              {brokerGiveaway.badge}
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold uppercase leading-none">
              {brokerGiveaway.headlinePrefix}
              <span className="text-campaign-gold">{brokerGiveaway.headlineHighlight}</span>
            </h2>
            <p className="mt-2.5 text-sm text-white/80 max-w-md">{brokerGiveaway.subcopy}</p>

            <div className="mt-4 flex flex-col gap-2.5 max-w-md">
              {brokerGiveaway.entryRules.map((rule) => (
                <div
                  key={rule.label}
                  className={clsx(
                    'relative flex items-center gap-3 rounded-xl px-3 py-2.5 border bg-black/20',
                    rule.bonus ? 'border-campaign-gold/50' : 'border-white/10'
                  )}>
                  {rule.bonus && (
                    <span className="absolute -top-2 left-3 bg-campaign-card text-campaign-gold text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded">
                      Bonus
                    </span>
                  )}
                  <span
                    className={clsx(
                      'w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0',
                      rule.bonus ? 'bg-campaign-gold text-campaign-from' : 'bg-white/20 text-white'
                    )}>
                    {rule.bonus ? (
                      <PlusIcon className="w-3.5 h-3.5" />
                    ) : (
                      <CheckIcon className="w-3.5 h-3.5" />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold leading-tight">{rule.label}</div>
                    <div className="text-xs text-white/60">{rule.sublabel}</div>
                  </div>
                  <span
                    className={clsx(
                      'text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap',
                      rule.bonus ? 'bg-campaign-gold text-campaign-from' : 'bg-white text-campaign-from'
                    )}>
                    {rule.entries}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Button
                className="bg-white text-primary normal-case font-bold rounded-lg text-sm py-2.5 px-6"
                onClick={onAddLenders}>
                Add lenders →
              </Button>
              {canCreateInspection && (
                <Button
                  variant="outlined"
                  className="bg-transparent border border-white/50 text-white normal-case font-semibold rounded-lg text-sm py-2.5 px-6"
                  onClick={() => navigate('/inspections/create')}>
                  New inspection
                </Button>
              )}
            </div>
          </div>

          {/* Right — prize card */}
          <div className="w-full flex flex-col items-center lg:w-72 lg:flex-shrink-0">
            <div className="relative w-full max-w-[280px]">
              <div className="bg-campaign-card rounded-2xl p-4 min-h-[168px] flex flex-col justify-between shadow-xl">
                <div className="flex justify-between">
                  <span className="w-8 h-8 rounded-lg bg-campaign-gold/20 flex items-center justify-center text-base">🎁</span>
                  <span className="w-8 h-8 rounded-lg bg-primary/25 flex items-center justify-center text-base">💳</span>
                </div>
                <div>
                  <div className="text-3xl font-extrabold">{brokerGiveaway.prizeAmount}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/55 mt-0.5">
                    {brokerGiveaway.prizeLabel}
                  </div>
                </div>
              </div>
              <div className="absolute -right-3 -bottom-3 z-10 w-11 h-11 rounded-full bg-white text-primary flex items-center justify-center shadow-lg">
                <CheckIcon className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-6 bg-white text-grey-900 text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 rounded-full">
              {brokerGiveaway.backedByLabel.split(/(authentik8)/i).map((part, i) =>
                /^authentik8$/i.test(part) ? (
                  <span key={i} className="text-secondary">
                    {part}
                  </span>
                ) : (
                  part
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrokerPromoPanel;
